/**
 * Live conversation state hook.
 *
 * Generic (module-agnostic) driver for an AI-led, time-boxed spoken dialogue
 * (the live-conversation capability, LCT-002/003/004/008):
 *  - runs a configurable thinking window before the conversation starts,
 *  - opens the authenticated `/ws/conversation` bridge,
 *  - streams microphone audio as PCM16 and plays the AI audio it receives,
 *  - renders live, speaker-attributed turns,
 *  - enforces the client-side cap (the server enforces the hard cap) and
 *    handles early-end, completion, and errors.
 *
 * It owns no exam/grading concerns — the Speaking module routes here for tagged
 * conversation tasks and persistence happens server-side on finalize.
 *
 * @module useLiveConversationState
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { logger } from '@/lib/logger';

/** Realtime audio is mono signed-16-bit PCM at 24 kHz (matches the backend bridge). */
const SAMPLE_RATE = 24000;

export type LiveConversationMode =
  | 'thinking'
  | 'connecting'
  | 'active'
  | 'finalizing'
  | 'done'
  | 'error';

export interface LiveConversationTurn {
  readonly role: 'ai' | 'user';
  readonly text: string;
}

export interface LiveConversationParams {
  /** Stable simulation session id. */
  readonly sessionId: string | null;
  /** Backend question UUID. */
  readonly questionId: string | null;
  /** Question/task text shown to the candidate and sent to the AI. */
  readonly questionText: string;
  /** Optional planning notes shown alongside the question. */
  readonly notes?: string | null;
  readonly module?: string;
  readonly section?: number | null;
  readonly level?: string | null;
  readonly examType?: string | null;
  /** Thinking window in seconds (LCT-002, default 90). */
  readonly thinkingSeconds?: number;
  /** Hard cap in seconds (LCT-004, default 240). */
  readonly maxSeconds?: number;
  /** Blocks the flow when voice consent is missing. */
  readonly disabled?: boolean;
  /** Called once the conversation has been finalized (success or partial). */
  readonly onFinalized?: () => void;
}

interface LiveConversationState {
  mode: LiveConversationMode;
  thinkingTimeLeft: number;
  conversationTimeLeft: number;
  turns: LiveConversationTurn[];
  aiSpeaking: boolean;
  userSpeaking: boolean;
  error: string | null;
}

function buildConversationWsUrl(): string {
  const { protocol, host } = window.location;
  const wsProtocol = protocol === 'https:' ? 'wss:' : 'ws:';
  return `${wsProtocol}//${host}/ws/conversation`;
}

/** Base64-encode raw bytes in chunks (avoids call-stack limits on large buffers). */
function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** Float32 [-1,1] samples -> little-endian signed 16-bit PCM bytes. */
function floatTo16BitPcm(input: Float32Array): Uint8Array {
  const out = new DataView(new ArrayBuffer(input.length * 2));
  for (let i = 0; i < input.length; i += 1) {
    const s = Math.max(-1, Math.min(1, input[i]));
    out.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return new Uint8Array(out.buffer);
}

export function useLiveConversationState(params: LiveConversationParams) {
  const {
    sessionId,
    questionId,
    questionText,
    notes,
    module = 'speaking',
    section,
    level,
    examType,
    thinkingSeconds = 90,
    maxSeconds = 240,
    disabled = false,
    onFinalized,
  } = params;

  const [state, setState] = useState<LiveConversationState>({
    mode: 'thinking',
    thinkingTimeLeft: thinkingSeconds,
    conversationTimeLeft: maxSeconds,
    turns: [],
    aiSpeaking: false,
    userSpeaking: false,
    error: null,
  });

  const wsRef = useRef<WebSocket | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const captureContextRef = useRef<AudioContext | null>(null);
  const playbackContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const playbackCursorRef = useRef<number>(0);
  // Turns the "Partner speaking" indicator off when the scheduled AI audio
  // actually finishes (see playAiAudio) rather than when the turn-done event
  // arrives — chunks stream in faster than realtime, so the audio is still
  // playing well after the model is done generating.
  const aiSpeakingOffTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const aiTurnTextRef = useRef<string>('');
  const startedRef = useRef<boolean>(false);
  const finalizedRef = useRef<boolean>(false);

  const teardownAudio = useCallback(() => {
    if (aiSpeakingOffTimerRef.current) {
      clearTimeout(aiSpeakingOffTimerRef.current);
      aiSpeakingOffTimerRef.current = null;
    }
    processorRef.current?.disconnect();
    processorRef.current = null;
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }
    void captureContextRef.current?.close().catch(() => undefined);
    captureContextRef.current = null;
    void playbackContextRef.current?.close().catch(() => undefined);
    playbackContextRef.current = null;
    playbackCursorRef.current = 0;
  }, []);

  const closeSocket = useCallback(() => {
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws && ws.readyState <= WebSocket.OPEN) {
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    }
  }, []);

  /** Schedule a chunk of AI PCM16 audio for gapless playback. */
  const playAiAudio = useCallback((base64: string) => {
    const ctx = playbackContextRef.current;
    if (!ctx) return;
    const bytes = base64ToBytes(base64);
    const pcm = new Int16Array(bytes.buffer, bytes.byteOffset, Math.floor(bytes.byteLength / 2));
    if (pcm.length === 0) return;
    const buffer = ctx.createBuffer(1, pcm.length, SAMPLE_RATE);
    const channel = buffer.getChannelData(0);
    for (let i = 0; i < pcm.length; i += 1) {
      channel[i] = pcm[i] / 0x8000;
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    const now = ctx.currentTime;
    const startAt = Math.max(now, playbackCursorRef.current);
    source.start(startAt);
    playbackCursorRef.current = startAt + buffer.duration;
    setState((prev) => (prev.aiSpeaking ? prev : { ...prev, aiSpeaking: true }));

    // Keep "Partner speaking" lit for the real playback duration. Each new chunk
    // pushes the tail out; we (re)arm a single timer for when the scheduled audio
    // finishes, so the indicator stays on while the partner is actually heard.
    if (aiSpeakingOffTimerRef.current) clearTimeout(aiSpeakingOffTimerRef.current);
    const msUntilDone = Math.max(0, (playbackCursorRef.current - ctx.currentTime) * 1000) + 150;
    aiSpeakingOffTimerRef.current = setTimeout(() => {
      aiSpeakingOffTimerRef.current = null;
      setState((prev) => (prev.aiSpeaking ? { ...prev, aiSpeaking: false } : prev));
    }, msUntilDone);
  }, []);

  const handleServerMessage = useCallback(
    (message: Record<string, unknown>) => {
      const type = message.type as string;
      switch (type) {
        case 'ready':
          setState((prev) => ({ ...prev, mode: 'active' }));
          break;
        case 'ai_audio':
          if (typeof message.audio === 'string') playAiAudio(message.audio);
          break;
        case 'ai_transcript_delta':
          aiTurnTextRef.current += (message.text as string) || '';
          break;
        case 'ai_turn_done': {
          // Commit the transcript turn, but DON'T clear aiSpeaking here — the
          // buffered audio is usually still playing. playAiAudio's tail timer
          // turns the indicator off when the partner is actually finished.
          const text = aiTurnTextRef.current.trim();
          aiTurnTextRef.current = '';
          setState((prev) => ({
            ...prev,
            turns: text ? [...prev.turns, { role: 'ai', text }] : prev.turns,
          }));
          break;
        }
        case 'user_speech_started':
          setState((prev) => ({ ...prev, userSpeaking: true }));
          break;
        case 'user_speech_stopped':
          setState((prev) => ({ ...prev, userSpeaking: false }));
          break;
        case 'user_transcript': {
          const text = ((message.text as string) || '').trim();
          setState((prev) => ({
            ...prev,
            userSpeaking: false,
            turns: text ? [...prev.turns, { role: 'user', text }] : prev.turns,
          }));
          break;
        }
        case 'finalized':
          finalizedRef.current = true;
          setState((prev) => ({ ...prev, mode: 'done', aiSpeaking: false, userSpeaking: false }));
          teardownAudio();
          onFinalized?.();
          break;
        case 'error':
          setState((prev) => ({
            ...prev,
            mode: 'error',
            error: (message.message as string) || 'Conversation error',
          }));
          break;
        default:
          break;
      }
    },
    [playAiAudio, teardownAudio, onFinalized],
  );

  /** Capture the mic as PCM16 and stream frames to the bridge. */
  const startMicCapture = useCallback(async (): Promise<boolean> => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      return false;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      micStreamRef.current = stream;
      const AudioCtx =
        window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const captureCtx = new AudioCtx({ sampleRate: SAMPLE_RATE });
      captureContextRef.current = captureCtx;
      const source = captureCtx.createMediaStreamSource(stream);
      const processor = captureCtx.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      processor.onaudioprocess = (event) => {
        const ws = wsRef.current;
        if (ws?.readyState !== WebSocket.OPEN) return;
        const input = event.inputBuffer.getChannelData(0);
        const pcm = floatTo16BitPcm(input);
        ws.send(JSON.stringify({ type: 'audio', audio: bytesToBase64(pcm) }));
      };
      source.connect(processor);
      processor.connect(captureCtx.destination);
      return true;
    } catch (error) {
      logger.error('Live conversation mic capture failed', error);
      return false;
    }
  }, []);

  const connect = useCallback(async () => {
    if (startedRef.current || disabled) return;
    if (!sessionId || !questionId) {
      setState((prev) => ({ ...prev, mode: 'error', error: 'Missing session or question' }));
      return;
    }
    startedRef.current = true;
    setState((prev) => ({ ...prev, mode: 'connecting' }));

    const AudioCtx =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    playbackContextRef.current = new AudioCtx({ sampleRate: SAMPLE_RATE });

    const micOk = await startMicCapture();
    if (!micOk) {
      setState((prev) => ({ ...prev, mode: 'error', error: 'Microphone unavailable' }));
      startedRef.current = false;
      return;
    }

    const ws = new WebSocket(buildConversationWsUrl());
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(
        JSON.stringify({
          type: 'init',
          sessionId,
          questionId,
          questionText,
          notes: notes ?? null,
          module,
          section: section ?? null,
          level: level ?? null,
          examType: examType ?? null,
          maxSeconds,
        }),
      );
    };
    ws.onmessage = (event) => {
      try {
        handleServerMessage(JSON.parse(event.data));
      } catch (error) {
        logger.warn('Failed to parse conversation message', error);
      }
    };
    ws.onerror = (event) => {
      logger.error('Conversation websocket error', event);
    };
    ws.onclose = () => {
      // The server finalizes before closing; if it closed without a finalize
      // event the session ended abnormally — surface it but keep any turns.
      if (!finalizedRef.current) {
        setState((prev) =>
          prev.mode === 'done' || prev.mode === 'error'
            ? prev
            : { ...prev, mode: 'finalizing' },
        );
      }
      teardownAudio();
    };
  }, [
    disabled,
    sessionId,
    questionId,
    questionText,
    notes,
    module,
    section,
    level,
    examType,
    maxSeconds,
    startMicCapture,
    handleServerMessage,
    teardownAudio,
  ]);

  /** Candidate ends early (LCT-004); the server finalizes what was captured. */
  const endConversation = useCallback(() => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'end' }));
    }
    setState((prev) =>
      prev.mode === 'active' || prev.mode === 'connecting'
        ? { ...prev, mode: 'finalizing' }
        : prev,
    );
  }, []);

  /** Skip the thinking window and start immediately. */
  const startNow = useCallback(() => {
    setState((prev) => (prev.mode === 'thinking' ? { ...prev, thinkingTimeLeft: 0 } : prev));
  }, []);

  // Thinking countdown -> auto-connect when it elapses (LCT-002).
  useEffect(() => {
    if (state.mode !== 'thinking') return undefined;
    if (state.thinkingTimeLeft <= 0) {
      void connect();
      return undefined;
    }
    const timer = setInterval(() => {
      setState((prev) => ({ ...prev, thinkingTimeLeft: Math.max(0, prev.thinkingTimeLeft - 1) }));
    }, 1000);
    return () => clearInterval(timer);
  }, [state.mode, state.thinkingTimeLeft, connect]);

  // Conversation cap countdown (client mirror of the server's hard cap, LCT-004).
  useEffect(() => {
    if (state.mode !== 'active') return undefined;
    if (state.conversationTimeLeft <= 0) {
      endConversation();
      return undefined;
    }
    const timer = setInterval(() => {
      setState((prev) => ({
        ...prev,
        conversationTimeLeft: Math.max(0, prev.conversationTimeLeft - 1),
      }));
    }, 1000);
    return () => clearInterval(timer);
  }, [state.mode, state.conversationTimeLeft, endConversation]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      closeSocket();
      teardownAudio();
    };
  }, [closeSocket, teardownAudio]);

  return {
    mode: state.mode,
    thinkingTimeLeft: state.thinkingTimeLeft,
    conversationTimeLeft: state.conversationTimeLeft,
    turns: state.turns,
    aiSpeaking: state.aiSpeaking,
    userSpeaking: state.userSpeaking,
    error: state.error,
    startNow,
    endConversation,
  };
}
